import React, { useState } from "react";
import { useContext } from "react";
import { LoginContext } from "context/Auth/LoginContext";
import { useTranslation } from "react-i18next";
import { User, Envelope, Phone, CheckCircle } from "@phosphor-icons/react";
import usePost from "Hooks/usePost";
import Spinner from "Components/RequestHandler/Spinner";
import { StatusHandler } from "Components/ApiNotification";

const EditProfile = () => {
  const { user, token, setUser } = useContext(LoginContext);
  const { t } = useTranslation();
  const { postData, isLoading, isError, data } = usePost();

  const [formData, setFormData] = useState({
    fullName: user?.fullName || "",
    email: user?.email || "",
    phoneNumber: user?.phoneNumber || "",
  });

  const [isEditing, setIsEditing] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setUpdateSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const response = await postData("auth/update-profile", formData, token);

    if (response?.success) {
      setUpdateSuccess(true);
      setIsEditing(false);
      // Update local user state
      setUser((prev) => ({ ...prev, ...formData }));
      // Update localStorage
      const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
      localStorage.setItem("user", JSON.stringify({ ...storedUser, ...formData }));
    }
  };

  const handleCancel = () => {
    setFormData({
      fullName: user?.fullName || "",
      email: user?.email || "",
      phoneNumber: user?.phoneNumber || "",
    });
    setIsEditing(false);
  };

  return (
    <div className="font-text">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl md:text-2xl font-bold text-gray-900">
          {t("account.editProfile")}
        </h2>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="px-4 py-2 text-sm font-medium text-primary hover:bg-primary/5 rounded-lg transition-colors"
          >
            {t("common.edit")}
          </button>
        )}
      </div>

      <StatusHandler error={isError} data={data} />

      {updateSuccess && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
          <CheckCircle size={20} weight="fill" />
          <span>{t("account.profileUpdated") || "Profile updated successfully!"}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Full Name Field */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t("common.fullName")}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <User size={20} className="text-gray-400" />
            </div>
            <input
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              disabled={!isEditing}
              className={`w-full pl-10 pr-4 py-3 border rounded-lg transition-colors ${
                isEditing
                  ? "border-gray-300 focus:border-primary focus:ring-1 focus:ring-primary bg-white"
                  : "border-gray-200 bg-gray-50 text-gray-600"
              }`}
              placeholder={t("common.fullName")}
            />
          </div>
        </div>

        {/* Email Field */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t("common.email")}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Envelope size={20} className="text-gray-400" />
            </div>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              disabled={!isEditing}
              className={`w-full pl-10 pr-4 py-3 border rounded-lg transition-colors ${
                isEditing
                  ? "border-gray-300 focus:border-primary focus:ring-1 focus:ring-primary bg-white"
                  : "border-gray-200 bg-gray-50 text-gray-600"
              }`}
              placeholder={t("common.email")}
            />
          </div>
        </div>

        {/* Phone Field */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t("common.phone")}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Phone size={20} className="text-gray-400" />
            </div>
            <input
              type="tel"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              disabled={!isEditing}
              className={`w-full pl-10 pr-4 py-3 border rounded-lg transition-colors ${
                isEditing
                  ? "border-gray-300 focus:border-primary focus:ring-1 focus:ring-primary bg-white"
                  : "border-gray-200 bg-gray-50 text-gray-600"
              }`}
              placeholder={t("common.phone")}
            />
          </div>
        </div>

        {/* Action Buttons */}
        {isEditing && (
          <div className="flex items-center gap-3 pt-4">
            <button
              type="submit"
              disabled={isLoading}
              className="px-6 py-2.5 bg-primary text-white font-medium rounded-lg hover:bg-primaryDark transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {isLoading ? <Spinner isSmall /> : t("common.save")}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-2.5 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
            >
              {t("common.cancel")}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default EditProfile;
